/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    IFieldDefinitionDropdownItem,
    IFieldDefinitionPanelOptionsData,
    IFieldDefinitionPanelOptionsState
} from ".";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";
import { v4 as uuid } from "uuid";
const logger: ILogger = getLogger();

export class FieldDefinitionPanelOptionsCommands extends BaseCommands<
    IFieldDefinitionPanelOptionsData,
    IFieldDefinitionPanelOptionsState
> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd(this.OnBlurHandler),
            onTextChanged: cb.bindCmd(this.OnTextChangedHandler),
            onAccessLevelChanged: cb.bindCmd(this.OnAccessLevelChangedHandler),
            onHelpTextChange: cb.bindCmd(this.OnHelpTextChangeHandler),
            onAuditChanged: cb.bindCmd(this.OnAuditChangedHandler),
            onAddDropdownItem: cb.bindCmdNP(this.onAddDropdownItemHandler),
            onRemoveDropdownItem: cb.bindCmd(this.onRemoveDropdownItemHandler),
            onDropdownItemTextChanged: cb.bindCmd(this.onDropdownItemTextChangedHandler),
            onDropdownItemValueChanged: cb.bindCmd(this.onDropdownItemValueChangedHandler),
            onDropdownItemDefaultKeyChanged: cb.bindCmd(this.onDropdownItemDefaultKeyChangedHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelOptionsState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Creates object containing text data to save
     * @param state Old state
     * @returns State data to save
     */
    Save = (state: IFieldDefinitionPanelOptionsState): IFieldDefinitionPanelOptionsData => {
        logger.logInformation("Saving drop down properties");

        // important to make a copy!
        const saveData: IFieldDefinitionPanelOptionsData = {
            ...state.data
        };

        return saveData;
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(
        state: IFieldDefinitionPanelOptionsState
    ): FormErrors<IFieldDefinitionPanelOptionsData> {
        const errors: FormErrors<IFieldDefinitionPanelOptionsData> = {};

        // settingName validation
        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Setting name validation function not hooked up");
                throw new Error("Unable to validate setting name");
            }

            errors.settingName = state.common.validateSettingName(
                state.data.settingName,
                state.common.originalSettingName
            );
        }

        // label
        if (state.data.title === "") {
            errors.title = "Label is required.";
        }

        // help text
        if (!state.data.helpTextValid) {
            errors.helpTextValid = "Help text is not valid";
        }

        if (state.data.dropDownItems?.some(i => i.text === "" || i.value === "")) {
            errors.dropDownItems = "Option text and value are required.";
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onDropdownItemTextChangedHandler = (
        state: IFieldDefinitionPanelOptionsState,
        changedItem: IFieldDefinitionDropdownItem
    ) => {
        const changed = state.data.dropDownItems?.find(item => item.key === changedItem.key);

        if (changed !== undefined) {
            changed.text = changedItem.text;
            state.isDirty = true;
        }

        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onDropdownItemValueChangedHandler = (
        state: IFieldDefinitionPanelOptionsState,
        changedItem: IFieldDefinitionDropdownItem
    ) => {
        const key = changedItem.key;
        const changed = state.data.dropDownItems?.find(item => item.key === key);

        if (changed !== undefined) {
            changed.value = changedItem.value;
            state.isDirty = true;
            if (state.data.defaultKey === key) {
                state.data.defaultValue = changedItem.value;
            }
        }
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onDropdownItemDefaultKeyChangedHandler = (
        state: IFieldDefinitionPanelOptionsState,
        defaultKey: string
    ) => {
        const defaultDropdownItem = state.data.dropDownItems?.find(item => item.key === defaultKey);

        if (defaultDropdownItem !== undefined) {
            state.data.defaultKey = defaultKey;
            state.data.defaultValue = defaultDropdownItem.value;
            state.isDirty = true;
        }
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onAddDropdownItemHandler = (state: IFieldDefinitionPanelOptionsState) => {
        const newItem: IFieldDefinitionDropdownItem = {
            key: uuid(),
            text: "",
            value: ""
        };

        state.data.dropDownItems.push(newItem);
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);

        return state;
    };

    onRemoveDropdownItemHandler = (
        state: IFieldDefinitionPanelOptionsState,
        keyToRemove: string
    ) => {
        state.data.dropDownItems = state.data.dropDownItems?.filter(
            item => item.key != keyToRemove
        );

        if (state.data.defaultKey == keyToRemove) {
            state.data.defaultKey = "";
            state.data.defaultValue = "";
        }
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };
}
